<template>
  <div
    class="mb-8"
    data-cy="listing"
  >
    <v-card
      border
      flat
      tile
    >
      <v-card-text class="mb-1">
        <v-row class="d-flex align-start">
          <v-col
            cols="12"
            md="8"
          >
            <div class="c-black fs-26 fw-600 mb-3">
              {{ provider.name }}

              <StandardButton
                v-if="!$store.state.pages.Provider?.features.enable_profile_lock"
                id="edit_profile_link"
                :ripple="false"
                :to="{
                  name: 'ProviderShow',
                  params: {
                    providerId: provider.id,
                  },
                  query: {
                    tab: 'overview',
                  },
                }"
                aria-label="Edit profile"
                class="mb-1"
                icon="edit"
                size="x-small"
                variant="text"
                tracked
              />
            </div>
            <div class="d-flex align-center fs-16">
              <v-icon
                class="me-2"
                color="primary"
                icon="location_on"
              />

              {{ location || $bct('Location unknown') }}
            </div>
          </v-col>

          <v-col
            cols="12"
            md="4"
          >
            <div class="d-flex justify-end mb-3">
              <template v-if="showUpdateOpenings">
                <v-spacer />
                <StandardButton
                  @click="handleUpdateOpenings()"
                  :block="$vuetify.display.smAndDown"
                  color="primary"
                  data-cy="update_availability"
                  variant="flat"
                >
                  {{
                    $bct(
                      useCombinedOpeningsAndEnrollments
                        ? 'Update openings & enrollments'
                        : 'Update openings',
                    )
                  }}
                </StandardButton>
              </template>
            </div>

            <div class="d-flex justify-end">
              <LongDate
                :date="provider.updated_at"
                class="c-light-black font-italic fs-12"
                prefix="Last updated"
                short-month
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <template v-if="showEnrollmentStatusesAndProgramInfo">
        <v-row
          v-if="interestedDashboardAvailable"
          class="d-flex fs-16 px-2"
        >
          <v-col
            v-for="group in dashboard.enrollment_status_groups"
            :key="group.status"
            class="border-sm"
            style="min-width: 120px; min-height: 100px"
          >
            <v-row dense>
              <v-col class="d-flex justify-center">
                {{ $bct(getStatusText(group.status)) }}
              </v-col>
            </v-row>
            <v-row dense>
              <v-col class="d-flex justify-center fs-20">
                <router-link
                  v-if="group.status == 'Enrolled'"
                  :to="{
                    name: 'EnrollmentIndex',
                    query: {
                      providerId: provider.id,
                      subsidyProgramId: subsidyProgram?.id,
                    },
                  }"
                >
                  {{ group.count || 0 }}
                </router-link>

                <router-link
                  v-else
                  :to="{
                    name: 'InterestedIndex',
                    query: {
                      providerId: provider.id,
                      status: group.status,
                      subsidyProgramId: subsidyProgram?.id,
                    },
                  }"
                >
                  {{ group.count || 0 }}
                </router-link>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-card
          v-if="enrolledDashboardAvailable"
          class="bg-primary-extra-light mt-3"
          tile
        >
          <v-card-title class="fs-20">
            {{ $bct(terms.programs) }}
          </v-card-title>

          <v-card-text class="pt-2">
            <v-row class="d-flex align-center">
              <v-col
                v-for="(group, index) in dashboard.enrollment_program_groups"
                :key="index"
                class="pt-0"
                cols="12"
                md="4"
                sm="6"
              >
                <ProgramSeatCount
                  @update:seats="loadDashboard()"
                  :enrollment-program-group="group"
                  :provider-id="provider.id"
                  :seat-types="seatTypes"
                  :subsidy-program="subsidyProgram"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>

      <template v-if="onboardingEnabled">
        <v-divider />
        <v-alert
          v-if="onboardingCompleted"
          :title="$bct('Completed: ' + $store.state.site.onboarding.title)"
          border="start"
          border-color="green"
          class="mb-0"
          color="surface"
          icon="check_circle"
          type="success"
          prominent
          tile
        >
          <template #append>
            <StandardButton
              :append-icon="provider?.onboarding_form?.locked ? 'lock' : 'lock_open'"
              :to="{
                name: 'FormNew',
                params: {
                  providerId: provider.id,
                  schemaId: $store.state.site.onboarding.schema_id,
                },
              }"
              color="primary"
              variant="text"
            >
              <span class="underlined">{{ $bct('Review') }}</span>
            </StandardButton>
          </template>
        </v-alert>
        <v-alert
          v-else
          :text="$bct($store.state.site.onboarding.description)"
          :title="$bct($store.state.site.onboarding.title)"
          border="start"
          border-color="primary"
          class="mb-0"
          color="surface"
          icon="notifications"
          type="success"
          prominent
          tile
        >
          <template #append>
            <StandardButton
              :to="{
                name: 'FormNew',
                params: {
                  providerId: provider.id,
                  schemaId: $store.state.site.onboarding.schema_id,
                },
              }"
              append-icon="east"
              color="primary"
              text="Get started"
            />
          </template>
        </v-alert>
      </template>
    </v-card>

    <v-dialog
      v-model="availabilityDialogIsVisible"
      content-class="mx-0"
      max-width="700px"
      scrim="transparent"
    >
      <ProgramOpeningsLegacy
        @change:openings-confirmed="confirmOpeningsAndEnrollments($event)"
        @change:openings-count="openingsCount = $event"
        @close="availabilityDialogIsVisible = false"
        ref="openings"
        :openings-confirmed-at="openingsConfirmedAt"
        :programs="programs"
        :provider-id="provider.id"
        :subsidy-program-id="subsidyProgram?.id"
        dialog
      />
    </v-dialog>

    <OpeningsEnrollmentSummaryDialog
      @openings-confirmed="confirmOpeningsAndEnrollments('Openings confirmed')"
      ref="summaryDialog"
      :programs="programs"
      :provider="provider"
    />
  </div>
</template>

<script setup>
import { useStore } from 'vuex';

import { formatAddress } from '@/shared/services/address';
import LongDate from '@/shared/components/LongDate.vue';
import OpeningsEnrollmentSummaryDialog from '@/shared/components/provider/OpeningsEnrollmentSummaryDialog.vue';
import ProgramOpeningsLegacy from '@/shared/components/provider/ProgramOpeningsLegacy.vue';
import { ProgramParams } from '@/shared/services/program-params';
import useApi from '@/shared/composables/useApi';
import useEnrollmentStatuses from '@/shared/composables/useEnrollmentStatuses';
import useEventBus from '@/shared/composables/useEventBus';
import useTerms from '@/shared/composables/useTerms';

import ProgramSeatCount from './program/ProgramSeatCount.vue';

const { api } = useApi();
const eventBus = useEventBus();
const { getStatusText } = useEnrollmentStatuses();
const store = useStore();
const { terms } = useTerms();

const $bct = inject('translator');

const props = defineProps({
  provider: {
    type: Object,
    default: null,
  },
  seatTypes: {
    type: Array,
    default: () => [],
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const availabilityDialogIsVisible = ref(false);
const dashboard = ref(null);
const openingsCount = ref(0);
const openingsConfirmedAt = ref(props.provider.openings_confirmed_at);
const programs = ref([]);
const summaryDialog = ref(null);

const enrolledDashboardAvailable = computed(() => {
  if (store.state.pages.Students?.enabled && dashboard.value) {
    return dashboard.value.enrollment_program_groups?.length > 0;
  }
  return false;
});

const interestedDashboardAvailable = computed(() => {
  if (store.state.pages.Interested?.enabled && dashboard.value) {
    return dashboard.value.enrollment_status_groups?.length > 0;
  }
  return false;
});

const location = computed(() => {
  return formatAddress(props.provider?.address1, props.provider?.city);
});

const onboardingEnabled = computed(() => {
  return (
    store.state.site.onboarding?.enabled &&
    props.provider?.schema_id === store.state.site.onboarding.provider_schema_id &&
    props.provider?.enable_onboarding
  );
});

const onboardingCompleted = computed(() => {
  return onboardingEnabled.value && !!props.provider?.onboarding_form?.submitted_at;
});

const showEnrollmentStatusesAndProgramInfo = computed(() => {
  return (
    props.subsidyProgram?.enable_provider_enrollment_status_summary &&
    (interestedDashboardAvailable.value || enrolledDashboardAvailable.value)
  );
});

const showUpdateOpenings = computed(() => {
  return (
    !store.state.pages.Provider?.features.enable_profile_lock &&
    store.state.schemas[props.provider.schema_id]?.meta?.openings.enabled
  );
});

const useCombinedOpeningsAndEnrollments = computed(() => {
  return store.state.config.enable_combined_openings_and_enrollments;
});

onMounted(load);

async function load() {
  await loadDashboard();
}

watch(() => props.subsidyProgram?.id, loadDashboard);

async function confirmOpeningsAndEnrollments(message) {
  const resp = await api.provider.update(props.provider.id, {
    openings_confirmed: true,
    openings_confirmed_by_id: store.state.profile.id,
  });

  openingsConfirmedAt.value = resp.data.openings_confirmed_at;
  summaryDialog.value.close();
  eventBus.chime(message);
}

async function loadPrograms() {
  const filters = {
    subsidy_program_id: props.subsidyProgram?.id,
  };
  const { data } = await api.program.index(props.provider.id, filters);
  programs.value = data.map((program) => new ProgramParams(program).asDraft());
}

async function loadDashboard() {
  const resp = await api.manager.provider.dashboard.get(
    props.provider.id,
    props.subsidyProgram?.id,
  );
  dashboard.value = resp.data;
}

function handleUpdateOpenings() {
  if (useCombinedOpeningsAndEnrollments.value) {
    void openSummaryDialog();
  } else {
    void openAvailabilityDialog();
  }
}

async function openSummaryDialog() {
  await loadPrograms();
  summaryDialog.value.open();
}

async function openAvailabilityDialog() {
  await loadPrograms();
  availabilityDialogIsVisible.value = true;
}
</script>
